'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';


import { getOrganizationSchema } from '@/lib/jsonld';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import SectionHeader from '@/components/ui/SectionHeader';

import Input from '@/components/ui/Input';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';


import TestimonialCard from './homepage/components/TestimonialCard';
import AppImage from '@/components/ui/AppImage';




export default function HomePage() {
  // Scroll reveal animation
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  // Lead magnet form state
  const [formData, setFormData] = useState({ name: '', email: '', company: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend/email service
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 3000);
  };

  // Testimonials carousel
  const [emblaRef] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  // Testimonials data — ordered by brand relevance: judgment, execution, strategic clarity first
  const testimonials = [
  {
    id: 'testimonial-7',
    quote: 'From the first day I met Daniel, I was impressed with his innate ability to analyze a situation and quickly assess the optimal solution. Not one to just accept the status quo, Daniel will educate himself on a topic and delve into the problems to truly understand the issue and create a unique and successful solution. I would highly recommend Daniel\'s work.',
    name: 'Craig Chilcote',
    title: 'Managing Partner',
    company: 'Anderson, Kuiti & Asuma P.L.L.C.',
    companyUrl: '',
    avatar: '/assets/images/craig-chilcote-180x180-1771401822004.jpg'
  },
  {
    id: 'testimonial-9',
    quote: 'I\'ve known Dan since he was a young hotshot starting his first company. He\'s one of the most driven producers of results I\'ve dealt with in the Internet marketing industry. His executive skills are impressive. I mentored him for a while when he got started, but now, if I need advice about managing a company, Dan is on my short list of people to call.',
    name: 'Randy Ray',
    title: 'CEO',
    company: 'Reflective Dynamics',
    companyUrl: '',
    avatar: '/assets/images/randy-ray-portrait-02-1771402065850.jpg'
  },
  {
    id: 'testimonial-10',
    quote: 'Daniel is honestly one of the most intelligent, driven, and hardest working individuals I\'ve met in the digital media business. This industry is filled with highly driven entrepreneurs and skilled professionals, yet Daniel consistently distinguishes himself with creativity and uncommon dedication to his work.',
    name: 'J. Todd',
    title: 'Executive Director',
    company: 'This Week in Digital Media',
    companyUrl: '',
    avatar: '/assets/images/j-todd-180x180-1771402130351.jpg'
  },
  {
    id: 'testimonial-6',
    quote: 'Daniel is one of the most savvy and creative SEO and online marketers I have come across in my career. He has a firm grasp of all online marketing activities designed to increase website traffic, improve conversions, and expand brand recognition across the Web. He is consistently developing new ways to reach customers using the latest Internet technologies. On top of his technical and creative expertise, Dan upholds the highest levels of integrity.',
    name: 'Mark Subel',
    title: 'Digital Marketing Consultant',
    company: 'Focal Click',
    companyUrl: '',
    avatar: '/assets/images/mark-subel-180x180-1771401981332.jpg'
  },
  {
    id: 'testimonial-1',
    quote: 'Daniel was probably one of the most successful performance marketers I have ever worked with, and I have worked with +20,000 of them. Daniel quickly identified a niche in a very competitive marketplace and became the best in that niche — a win-win situation for us both.',
    name: 'Edward Ihre',
    title: 'Owner & CEO',
    company: 'Ihre Consulting',
    companyUrl: '',
    avatar: '/assets/images/image-1771401264226.png'
  },
  {
    id: 'testimonial-8',
    quote: 'Daniel is great to work with — a savvy Internet professional who delivers results. He has a great knowledge base and is always watching out for smart opportunities and bringing ideas above and beyond our regular campaign. I have no hesitation in recommending him.',
    name: 'Rohit Prathinidhi',
    title: 'Partner Program Manager',
    company: 'PartyGaming',
    companyUrl: '',
    avatar: ''
  },
  {
    id: 'testimonial-2',
    quote: 'Working with Dan and the BOC team was a great experience. I was more than impressed with the integrity of their work and the success of their marketing campaigns. I highly recommend Best Odds Corp to anyone looking for comprehensive online advertising solutions.',
    name: 'Rick Peterson',
    title: 'Partner Manager',
    company: 'Affactive',
    companyUrl: '',
    avatar: ''
  },
  {
    id: 'testimonial-3',
    quote: 'Daniel has been producing phenomenal niche media websites for a long time, with some of the best niche content available online. He has grown his portfolio of sites and positioned himself as one of the leading performance consultants in the game.',
    name: 'Tom Galanis',
    title: 'Director',
    company: 'GameOn',
    companyUrl: '',
    avatar: ''
  },
  {
    id: 'testimonial-4',
    quote: 'Thanks to their extensive knowledge of SEO, exquisite lateral thinking, and innovative promotional ideas, the professional and friendly team of Best Odds Corp is a great partner to collaborate with.',
    name: 'Switch Affiliate Team',
    title: 'Partner Team',
    company: 'Switch Partner Team',
    companyUrl: '',
    avatar: ''
  },
  {
    id: 'testimonial-5',
    quote: 'It\'s been a pleasure collaborating with Seb and Daniel from Best Odds Corp. They are very prompt in responding to any questions and have always worked well with us in achieving the same goals. Seb and Daniel are all around great guys so it makes doing business with them an enjoyable experience.',
    name: 'Dione Scott-Rogers',
    title: 'Partner Manager',
    company: 'PKR',
    companyUrl: '',
    avatar: '/assets/images/dione-scott-rogers-pkr-1771401846346.jpg'
  }];



  const marketingServices = [
  {
    id: 'paid-media',
    icon: 'MegaphoneIcon',
    title: 'Paid Media',
    description: 'Google, Meta, LinkedIn campaigns that convert. No wasted spend.',
    slug: 'paid-media'
  },
  {
    id: 'content-production',
    icon: 'FilmIcon',
    title: 'Content Production',
    description: 'Video, photography, and creative assets. Shot in Las Vegas or on location.',
    slug: 'content-production'
  },
  {
    id: 'email-sms',
    icon: 'EnvelopeIcon',
    title: 'Email & SMS',
    description: 'Automated sequences, campaigns, and retention flows that drive revenue.',
    slug: 'email-sms'
  },
  {
    id: 'social-media',
    icon: 'ChatBubbleLeftRightIcon',
    title: 'Social Media',
    description: 'Organic and paid social strategy. Content calendars, community management.',
    slug: 'social-media'
  },
  {
    id: 'seo-content',
    icon: 'MagnifyingGlassIcon',
    title: 'SEO & Content',
    description: 'Technical SEO, content strategy, and editorial execution.',
    slug: 'seo-content'
  }];


  const aiServices = [
  {
    id: 'ai-automation',
    icon: 'CpuChipIcon',
    title: 'AI Automation',
    description: 'Custom workflows, chatbots, and process automation using AI.',
    slug: 'ai-automation'
  },
  {
    id: 'data-analytics',
    icon: 'ChartBarIcon',
    title: 'Data & Analytics',
    description: 'Business intelligence, dashboards, and predictive modeling.',
    slug: 'data-analytics'
  },
  {
    id: 'ai-integration',
    icon: 'BoltIcon',
    title: 'AI Integration',
    description: 'Integrate AI into your existing systems and workflows.',
    slug: 'ai-integration'
  }];


  // FAQ data
  const faqItems = [
  {
    id: 'faq-1',
    title: 'Do you require a monthly retainer?',
    content: 'No. Every engagement starts with a Sprint / Diagnostic — a paid strategic session that identifies your bottlenecks and delivers a prioritized roadmap. After the sprint, you choose your path: take the roadmap and execute internally, engage us for a fixed-scope buildout, or move into ongoing execution. No one is forced into a retainer before we understand your business.'
  },
  {
    id: 'faq-2',
    title: 'How do shoots work?',
    content: 'Production days (video/photo shoots) are scheduled separately and billed as project add-ons. A half-day shoot starts at $2,500, full-day at $5,000. Includes our Las Vegas-based crew, equipment, and post-production. Travel shoots available with advance notice.'
  },
  {
    id: 'faq-3',
    title: 'How is pricing determined?',
    content: 'Pricing reflects scope, speed, and business value — not just hours. A sprint is a fixed fee ($2,500–$15k+ depending on business size). Buildouts are scoped and quoted based on sprint findings ($10k–$75k+). Ongoing execution is monthly ($3k–$10k+/mo). DIY resources are subscription-based ($500–$1,500/mo). We don\'t charge only for time.'
  },
  {
    id: 'faq-4',
    title: 'What\'s DIY vs concierge?',
    content: 'DIY gives you our systems, templates, prompt packs, and async support — you execute. Concierge (Ongoing Execution, Buildout) means we do the work for you. Most clients start with a sprint to get clarity, then decide which path fits their situation.'
  }];


  // Services category state
  const [activeCategory, setActiveCategory] = useState('growth');

  const serviceCategories = [
    {
      id: 'growth',
      label: 'Growth Systems',
      eyebrow: 'Strategy-first',
      description: 'We identify your biggest constraint and build the systems that remove it — then scale what works.',
      services: [
        {
          name: 'Growth Strategy',
          hours: '20–35h',
          description: 'Bottleneck audit, channel prioritization, and a 90-day execution roadmap built for your business.',
        },
        {
          name: 'Follow-Up & Retention',
          hours: '18–30h',
          description: 'Automated email and SMS sequences that close the gap between lead and revenue — and keep clients coming back.',
        },
        {
          name: 'AI Automation',
          hours: '20–35h',
          description: 'Custom AI workflows that remove manual bottlenecks, automate follow-up, and free up your team.',
        },
        {
          name: 'GEO / AEO',
          hours: '12–20h',
          description: 'Optimize your content for AI-powered search engines — so you show up when it matters most.',
        },
      ],
    },
    {
      id: 'demand',
      label: 'Demand Generation',
      eyebrow: 'Paid + organic',
      description: 'Drive qualified traffic through paid and organic channels — built to convert, not just to spend.',
      services: [
        {
          name: 'Paid Search',
          hours: '10–16h',
          description: 'Google Ads campaigns with keyword targeting, ad creation, and conversion tracking.',
        },
        {
          name: 'Paid Social',
          hours: '12–20h',
          description: 'Multi-platform social ad campaigns with audience targeting and creative assets.',
        },
        {
          name: 'SEO',
          hours: '15–25h',
          description: 'Technical SEO audit, keyword strategy, and 90-day implementation roadmap.',
        },
      ],
    },
    {
      id: 'content',
      label: 'Content Engine',
      eyebrow: 'Built to publish',
      description: 'Turn your expertise into a consistent content engine — editorial, production, and distribution.',
      services: [
        {
          name: 'Content Systems',
          hours: '8–15h',
          description: 'Editorial calendar, production workflow, and distribution strategy that runs without you.',
        },
        {
          name: 'Photo & Video',
          hours: '25–50h',
          description: 'Professional photo and video content from concept through final delivery.',
        },
        {
          name: 'Social Media',
          hours: '15–25h',
          description: 'Social media strategy with content calendar and daily management.',
        },
      ],
    },
    {
      id: 'brand',
      label: 'Brand + Web',
      eyebrow: 'Foundation work',
      description: 'Build the visual and digital foundation that makes everything else perform better.',
      services: [
        {
          name: 'Branding & Design',
          hours: '30–50h',
          description: 'Complete brand identity package with logo, guidelines, and marketing collateral.',
        },
        {
          name: 'Web Design',
          hours: '40–80h',
          description: 'Custom website design with responsive development and CMS integration.',
        },
      ],
    },
    {
      id: 'print',
      label: 'Premium Print',
      eyebrow: 'Supporting',
      description: 'High-quality print materials that leave a lasting impression at every touchpoint.',
      services: [
        {
          name: 'Premium Print & Cards',
          hours: '3–8h',
          description: 'Professional print materials with premium finishes that make lasting impressions.',
        },
      ],
    },
  ];

  const activeCat = serviceCategories.find((c) => c.id === activeCategory) ?? serviceCategories[0];

  return (
    <>
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getOrganizationSchema()) }}
        suppressHydrationWarning />
      
      <main className="min-h-screen bg-background text-foreground">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden border-b border-border">
          {/* Full-bleed dark luxury background — CSS-only, no assets */}
          {/* Layer 1: Near-black charcoal base */}
          <div className="absolute inset-0" style={{ backgroundColor: '#0a0a0b' }} />
          {/* Layer 2: Subtle vignette — darkens edges, focuses center */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.65) 100%)',
            }}
          />
          {/* Layer 3: Restrained red ambient glow — behind headline zone only */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse 80% 55% at 50% 42%, rgba(185,28,28,0.13) 0%, rgba(185,28,28,0.04) 55%, transparent 80%)',
            }}
          />
          {/* Layer 4: Very subtle warm-to-cool depth gradient */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.25) 100%)',
            }}
          />

          <div className="relative z-10 w-full max-w-[1200px] mx-auto text-center px-4 sm:px-6 py-20 sm:py-28 md:py-32">
            <div className="reveal">
              {/* Proof badges */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-7 sm:mb-10">
                <Badge variant="primary">For Founders Doing $500K–$5M/yr</Badge>
                <Badge variant="default">Premium Local & Hospitality Businesses</Badge>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.0] sm:leading-[0.97] font-bold tracking-tighter mb-6 sm:mb-8 max-w-4xl mx-auto">
                You have revenue.{' '}
                <br className="hidden sm:block" />
                The operations are{' '}
                <span className="text-primary neon-glow">the problem.</span>
              </h1>

              {/* Pain-point subhead */}
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 sm:mb-5 leading-relaxed">
                Messy operations. Inconsistent marketing. Weak follow-up. Execution that never quite ships.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-4 sm:mb-5 leading-relaxed">
                We find the leaks, build the system, and execute every week — so you stop being the bottleneck.
              </p>

              {/* ICP qualifier */}
              <p className="text-xs sm:text-sm text-muted-foreground/55 max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed">
                Built for founder-led service businesses and premium operators in real estate, hospitality, med spas, and law — who have strong revenue but broken infrastructure.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-5 sm:mb-6">
                <Link href="/book" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_hero', cta_label: 'Book a Sprint' });
                  }
                }}>
                  <Button variant="primary" size="lg" className="w-full sm:w-auto border-glow">
                    Book a Sprint — Start With a Diagnostic
                  </Button>
                </Link>
                <Link href="/portfolio" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_hero', cta_label: 'See Our Work' });
                  }
                }}>
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                    See Our Work
                  </Button>
                </Link>
              </div>

              {/* Micro CTA */}
              <div className="mt-3 sm:mt-4">
                <Link href="/lp/90-day-blueprint" className="text-sm text-muted-foreground hover:text-foreground underline transition-colors" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_hero', cta_label: 'Get the 90-Day Blueprint' });
                  }
                }}>
                  Get the free 90-Day Growth Blueprint →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points / Who This Is For */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-28 px-4 sm:px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1400px] mx-auto">
            <div className="reveal text-center mb-10 sm:mb-14 md:mb-16">
              <p className="text-label uppercase tracking-widest text-primary mb-4">Sound familiar?</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                You're not stuck because you lack ambition.
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                You're stuck because the infrastructure hasn't caught up to the revenue.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 reveal">
              {[
                { pain: "You're the bottleneck", detail: 'Every decision runs through you. Nothing ships without your approval. Growth is capped at your bandwidth.' },
                { pain: 'Revenue is leaking', detail: 'Leads fall through the cracks. Follow-up is inconsistent. You know money is being left on the table.' },
                { pain: 'Marketing is reactive', detail: 'No content calendar. No system. You post when you remember and run ads when things slow down.' },
                { pain: 'Operations are messy', detail: "Processes live in people's heads. Handoffs break. The same problems keep coming back." },
                { pain: 'Content is under-leveraged', detail: 'You have expertise, results, and stories — but none of it is being turned into consistent content that converts.' },
                { pain: 'Execution is inconsistent', detail: "You've hired agencies. You've tried freelancers. Things start strong, then drift. Nothing sticks." },
              ].map((item) => (
                <div key={item.pain} className="p-5 sm:p-6 border border-border bg-background hover:border-primary transition-all duration-300">
                  <h3 className="text-foreground font-semibold mb-2 flex items-start gap-2">
                    <span className="text-primary mt-0.5">→</span>
                    {item.pain}
                  </h3>
                  <p className="text-body-sm text-muted-foreground">{item.detail}</p>
                </div>
              ))}
            </div>
            <div className="reveal text-center mt-10 sm:mt-12">
              <p className="text-muted-foreground mb-5 sm:mb-6 text-base sm:text-lg">
                If three or more of these hit — you're exactly who we built this for.
              </p>
              <Link href="/book">
                <Button variant="primary" size="lg" className="border-glow" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_pain', cta_label: 'Book a Sprint' });
                  }
                }}>
                  Book a Sprint — Let's Find the Bottleneck
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Phased Offer Stack */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-12 sm:mb-16 md:mb-20 reveal">
              <SectionHeader
                eyebrow="01 — How We Engage"
                title="The Sprint is the starting point."
                description="We don't sell retainers before we understand your business. Every engagement starts with a diagnostic sprint — then we map the right path forward together."
                align="center" />
            </div>

            {/* Phase flow indicator */}
            <div className="reveal flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10 sm:mb-14 md:mb-16 text-center">
              {['Sprint / Diagnostic', 'Buildout / Project', 'Ongoing Execution', 'DIY / Subscription'].map((phase, i) => (
                <div key={phase} className="flex items-center gap-2 sm:gap-3">
                  <div className="px-3 sm:px-4 py-2 border border-border bg-surface-1 text-xs uppercase tracking-widest text-primary font-semibold whitespace-nowrap">
                    {phase}
                  </div>
                  {i < 3 && <span className="text-primary/40 text-xl hidden sm:block">→</span>}
                </div>
              ))}
            </div>

            {/* 4 offer cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 mb-12 sm:mb-16">
              {/* Offer 1: Sprint */}
              <div className="reveal p-6 sm:p-8 border border-primary/60 hover:border-primary transition-all duration-300 bg-surface-1 group relative">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-primary" />
                <div className="mb-4">
                  <Badge variant="primary" className="text-xs mb-3">Phase 1 — Entry Point</Badge>
                </div>
                <h3 className="text-h4 mb-2">Sprint / Diagnostic</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary">$2,500</span>
                  <span className="text-body-sm text-muted-foreground"> – $15k+</span>
                </div>
                <p className="text-body-sm text-foreground mb-6">
                  A paid strategic diagnostic. We audit your business, identify the highest-value bottlenecks, and deliver a prioritized roadmap — before you commit to anything else.
                </p>
                <ul className="space-y-3 mb-8 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Revenue leak & bottleneck audit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Prioritized 90-day roadmap</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Delivered in 14 days</span>
                  </li>
                </ul>
                <Link href="/book" className="block w-full text-center px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-all text-label uppercase tracking-widest font-semibold" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_offers', cta_label: 'Book a Sprint', plan: 'Sprint' });
                  }
                }}>
                  Book a Sprint
                </Link>
              </div>

              {/* Offer 2: Buildout */}
              <div className="reveal p-6 sm:p-8 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group">
                <div className="mb-4">
                  <Badge variant="default" className="text-xs mb-3">Phase 2A — Implementation</Badge>
                </div>
                <h3 className="text-h4 mb-2">Buildout / Project</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary">$10k</span>
                  <span className="text-body-sm text-muted-foreground"> – $75k+</span>
                </div>
                <p className="text-body-sm text-foreground mb-6">
                  Fixed-scope implementation based on sprint findings. When the diagnostic uncovers a clear buildout — website, funnel, automation, content system — we scope and execute it.
                </p>
                <ul className="space-y-3 mb-8 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Scoped from sprint findings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Fixed price, defined deliverables</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Depth and access scale with your needs</span>
                  </li>
                </ul>
                <Link href="/pricing" className="block w-full text-center px-6 py-3 border border-border hover:border-primary hover:text-primary transition-all text-label uppercase tracking-widest font-semibold" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_offers', cta_label: 'See Buildout Pricing', plan: 'Buildout' });
                  }
                }}>
                  See Buildout Pricing
                </Link>
              </div>

              {/* Offer 3: Ongoing Execution */}
              <div className="reveal p-6 sm:p-8 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group">
                <div className="mb-4">
                  <Badge variant="default" className="text-xs mb-3">Phase 2B — Ongoing</Badge>
                </div>
                <h3 className="text-h4 mb-2">Ongoing Execution</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary">$3k</span>
                  <span className="text-body-sm text-muted-foreground"> – $10k+/mo</span>
                </div>
                <p className="text-body-sm text-foreground mb-6">
                  Monthly advisory, execution, and optimization for operators who want a consistent partner — not a one-time project. Depth and access scale with your needs.
                </p>
                <ul className="space-y-3 mb-8 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Weekly execution, not quarterly decks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Multi-channel campaigns & optimization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Reporting & continuous refinement</span>
                  </li>
                </ul>
                <Link href="/pricing" className="block w-full text-center px-6 py-3 border border-border hover:border-primary hover:text-primary transition-all text-label uppercase tracking-widest font-semibold" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_offers', cta_label: 'Explore Execution', plan: 'Ongoing' });
                  }
                }}>
                  Explore Execution
                </Link>
              </div>

              {/* Offer 4: DIY */}
              <div className="reveal p-6 sm:p-8 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group">
                <div className="mb-4">
                  <Badge variant="default" className="text-xs mb-3">Phase 3 — Self-Service</Badge>
                </div>
                <h3 className="text-h4 mb-2">DIY / Portal</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-primary">$500</span>
                  <span className="text-body-sm text-muted-foreground"> – $1,500/mo</span>
                </div>
                <p className="text-body-sm text-foreground mb-6">
                  Templates, systems, dashboards, prompt packs, SOPs, and frameworks — the scalable recurring layer for operators who want the playbook without the retainer.
                </p>
                <ul className="space-y-3 mb-8 text-body-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Pre-built workflows & templates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Prompt packs, SOPs & frameworks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Async support included</span>
                  </li>
                </ul>
                <Link href="/diy" className="block w-full text-center px-6 py-3 border border-border hover:border-primary hover:text-primary transition-all text-label uppercase tracking-widest font-semibold" onClick={() => {
                  if (typeof window !== 'undefined' && typeof (window as Window & {gtag?: Function;}).gtag === 'function') {
                    (window as Window & {gtag: Function;}).gtag('event', 'cta_click', { event_category: 'homepage_offers', cta_label: 'Browse DIY Resources', plan: 'DIY' });
                  }
                }}>
                  Browse DIY Resources
                </Link>
              </div>
            </div>

            {/* Three paths after the sprint */}
            <div className="reveal p-6 sm:p-8 md:p-10 border border-border bg-surface-1 max-w-4xl mx-auto">
              <h3 className="text-h4 text-center mb-6 sm:mb-8">Three paths after the Sprint</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
                <div className="text-center">
                  <div className="w-10 h-10 border border-primary flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-sm">01</span>
                  </div>
                  <h4 className="text-foreground font-semibold mb-2">Roadmap Only</h4>
                  <p className="text-body-sm text-muted-foreground">Take the sprint deliverables and execute internally. You own the plan.</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 border border-primary flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-sm">02</span>
                  </div>
                  <h4 className="text-foreground font-semibold mb-2">Project Implementation</h4>
                  <p className="text-body-sm text-muted-foreground">We scope and build the highest-priority work as a fixed-fee project.</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 border border-primary flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-sm">03</span>
                  </div>
                  <h4 className="text-foreground font-semibold mb-2">Ongoing Execution</h4>
                  <p className="text-body-sm text-muted-foreground">We become your execution partner — shipping every week, optimizing every month.</p>
                </div>
              </div>
              <div className="text-center mt-6 sm:mt-8">
                <Link href="/pricing" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  See full pricing for all paths →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Proof Section */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-12 sm:mb-16 reveal">
              <SectionHeader
                eyebrow="02 — Founder Credibility"
                title="20+ years of building, testing, and executing."
                description="Daniel Moravec founded Best Odds Corp in college and has spent over two decades building, selling, automating, and repositioning businesses."
                align="center" />
            </div>

            {/* Founder profile card */}
            <div className="reveal max-w-4xl mx-auto mb-12 sm:mb-16">
              <div className="border border-border bg-surface-1 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
                  {/* Left: Headshot + social */}
                  <div className="bg-surface-2 border-b md:border-b-0 md:border-r border-border flex flex-col items-center justify-center p-8 md:p-10 gap-6">
                    <div className="w-36 h-36 overflow-hidden border-2 border-primary shadow-glow flex-shrink-0">
                      <AppImage
                        src="/assets/images/Untitled-1771493284658.jpeg"
                        alt="Daniel Moravec — Founder of Best Odds Corp"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-foreground font-semibold text-h5 mb-1">Daniel Moravec</p>
                      <p className="text-body-sm text-muted-foreground mb-4">Founder & CEO</p>
                      <div className="flex flex-col gap-2">
                        <a
                          href="https://www.linkedin.com/in/danielmoravec/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary text-body-sm text-muted-foreground hover:text-foreground transition-all duration-200"
                        >
                          <svg className="w-4 h-4 text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          LinkedIn
                        </a>
                        <a
                          href="https://bestoddscorp.com/work-with-daniel"
                          className="inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary text-body-sm text-muted-foreground hover:text-foreground transition-all duration-200"
                        >
                          Work with Daniel →
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Right: Bio + credentials */}
                  <div className="p-6 sm:p-8 md:p-10">
                    <p className="text-body text-muted-foreground mb-6 leading-relaxed">
                      Formally trained in Marketing and Business Communication (B.S., The College of St. Scholastica). Over 20 years operating across business building, automation, content, trading, and execution. Active trader and content creator with a combined audience of 150,000+ across Afterhour and X. He personally built this website and the X Cleanup Console Chrome extension — using AI as leverage throughout.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                      {[
                        { label: 'Years Operating', value: '20+' },
                        { label: 'Afterhour Followers', value: '135,658+' },
                        { label: 'X Followers', value: '17,186+' },
                        { label: 'Degree', value: 'B.S. Marketing' },
                        { label: 'Founded', value: '2000' },
                        { label: 'Based', value: 'Las Vegas' },
                      ].map((item) => (
                        <div key={item.label} className="p-3 sm:p-4 border border-border bg-background text-center">
                          <div className="text-h4 text-primary mb-1">{item.value}</div>
                          <div className="text-micro uppercase tracking-widest text-muted-foreground leading-tight">{item.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6">
                      <Link href="/team" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                        Full founder profile →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Process credibility strip */}
            <div className="reveal mb-12 sm:mb-16">
              <h3 className="text-label uppercase tracking-widest text-center mb-6 sm:mb-8 text-muted-foreground">The Execution Process</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3 max-w-5xl mx-auto mb-8">
                {[
                  { step: '01', label: 'Audit' },
                  { step: '02', label: 'Bottleneck Analysis' },
                  { step: '03', label: 'Prioritization' },
                  { step: '04', label: 'Roadmap' },
                  { step: '05', label: 'Weekly Execution' },
                  { step: '06', label: 'Reporting' },
                  { step: '07', label: 'Refinement' },
                ].map((item) => (
                  <div key={item.step} className="p-3 sm:p-4 border border-border bg-surface-1 text-center hover:border-primary transition-colors">
                    <div className="text-primary font-bold text-sm mb-1">{item.step}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-widest leading-tight">{item.label}</div>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link href="/case-studies" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  See how the process works →
                </Link>
              </div>
            </div>

            {/* Testimonials preview carousel */}
            <div className="reveal mb-12 sm:mb-16">
              <h3 className="text-h3 text-center mb-8 sm:mb-12">What people say about Daniel</h3>
              <div className="max-w-4xl mx-auto">
                <div className="embla" ref={emblaRef}>
                  <div className="embla__container">
                    {testimonials.slice(0, 6).map((testimonial) => (
                    <div key={testimonial.id} className="embla__slide flex-[0_0_100%] min-w-0">
                        <TestimonialCard
                        quote={testimonial.quote}
                        name={testimonial.name}
                        title={testimonial.title}
                        company={testimonial.company}
                        companyUrl={testimonial.companyUrl}
                        avatar={testimonial.avatar} />
                      
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Link href="/testimonials">
                  <Button variant="secondary" size="md">
                    View All Testimonials
                  </Button>
                </Link>
              </div>
            </div>

            {/* Portfolio credibility strip */}
            <div className="reveal">
              <h3 className="text-label uppercase tracking-widest text-center mb-6 sm:mb-8 text-muted-foreground">From Our Portfolio</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 max-w-5xl mx-auto mb-8">
                <Link href="/portfolio" className="p-4 sm:p-5 border border-border bg-surface-1 hover:border-primary transition-all flex flex-col gap-2">
                  <span className="text-body-sm font-semibold text-foreground">StreetSaw.com</span>
                  <span className="text-xs text-muted-foreground">E-transportation electronics brand</span>
                </Link>
                <Link href="/portfolio" className="p-4 sm:p-5 border border-border bg-surface-1 hover:border-primary transition-all flex flex-col gap-2">
                  <span className="text-body-sm font-semibold text-foreground">Organic101</span>
                  <span className="text-xs text-muted-foreground">Natural bodycare & wellness brand</span>
                </Link>
                <Link href="/portfolio" className="p-4 sm:p-5 border border-border bg-surface-1 hover:border-primary transition-all flex flex-col gap-2">
                  <span className="text-body-sm font-semibold text-foreground">HealthcareUnlocked</span>
                  <span className="text-xs text-muted-foreground">Healthcare & PPE supply platform</span>
                </Link>
                <Link href="/portfolio" className="p-4 sm:p-5 border border-border bg-surface-1 hover:border-primary transition-all flex flex-col gap-2">
                  <span className="text-body-sm font-semibold text-foreground">VelvetAngels.com</span>
                  <span className="text-xs text-muted-foreground">Celebrity shoe brand — relaunching 2026</span>
                </Link>
                <Link href="/portfolio" className="p-4 sm:p-5 border border-border bg-surface-1 hover:border-primary transition-all flex flex-col gap-2">
                  <span className="text-body-sm font-semibold text-foreground">SkyhookCapital</span>
                  <span className="text-xs text-muted-foreground">Creative finance real estate</span>
                </Link>
              </div>
              <div className="text-center">
                <Link href="/portfolio" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  View Portfolio →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES PREVIEW */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="mb-12 sm:mb-16 reveal">
              <SectionHeader
                eyebrow="03 — What We Execute"
                title="We fix the leaks. Then we build the engine."
                description="We don't sell a menu of services. We identify your biggest constraint — operations, marketing, follow-up, content — and execute the right work every sprint."
                align="center" />
              
            </div>

            {/* Category Tabs */}
            <div className="reveal mb-8 sm:mb-10 flex flex-wrap justify-center gap-2">
              {serviceCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 sm:px-5 py-2 sm:py-2.5 text-body-sm font-medium border transition-all duration-200 ${
                    activeCategory === cat.id
                      ? 'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground bg-transparent'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Active Category Panel */}
            <div className="reveal">
              {/* Category description */}
              <div className="mb-6 sm:mb-8 text-center">
                <span className="text-micro uppercase tracking-widest text-primary mb-2 block">{activeCat.eyebrow}</span>
                <p className="text-body text-muted-foreground max-w-xl mx-auto">{activeCat.description}</p>
              </div>

              {/* Service cards for active category */}
              <div className={`grid gap-4 sm:gap-6 ${
                activeCat.services.length === 1
                  ? 'grid-cols-1 max-w-md mx-auto'
                  : activeCat.services.length === 2
                  ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto'
                  : activeCat.services.length === 3
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
              }`}>
                {activeCat.services.map((service) => (
                  <div
                    key={service.name}
                    className="p-5 sm:p-6 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="default" className="text-xs">{activeCat.label}</Badge>
                      <Badge variant="primary" className="text-xs">{service.hours}</Badge>
                    </div>
                    <h3 className="text-h5 mb-2">{service.name}</h3>
                    <p className="text-body-sm text-muted-foreground flex-1">{service.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA to full services menu */}
            <div className="reveal text-center mt-10 sm:mt-14">
              <Link href="/services/menu">
                <Button variant="primary" size="lg" className="border-glow">
                  View Full Services Menu
                </Button>
              </Link>
              <p className="text-body-sm text-muted-foreground mt-4">
                All services are scoped in sprints and priced by scope, speed, and business value.
              </p>
            </div>
          </div>
        </section>

        {/* SELECTED PORTFOLIO BUILDS */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-10 sm:mb-12">
              <SectionHeader
                eyebrow="04 — Examples of Execution"
                title="Sites, Systems, and Brands We've Built"
                description="These are real properties — built, grown, and operated by Best Odds Corp. No invented metrics. No fabricated clients. Real execution across e-commerce, media, services, and technology."
                align="center" />
            </div>

            {/* Portfolio cards — real properties */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8 mb-10 sm:mb-12">
              {/* StreetSaw */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">E-Commerce</Badge>
                  <Badge variant="default" className="text-xs">Electronics</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">StreetSaw.com</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Electronics brand selling hoverboards, electric scooters, and e-bikes. Built from the ground up — e-commerce platform, product SEO, paid media, and brand development.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['E-Commerce Platform', 'Paid Media', 'Brand Development', 'Product SEO'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://streetsaw.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit StreetSaw.com →
                </a>
              </div>

              {/* Organic101 */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">E-Commerce</Badge>
                  <Badge variant="default" className="text-xs">Wellness</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">Organic101.com</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Natural bodycare brand selling organic aluminum-free deodorants and luxury lip balms. Built for health-conscious consumers — e-commerce, content marketing, SEO, and brand identity.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['E-Commerce', 'Content Marketing', 'SEO', 'Brand Development'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://organic101.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit Organic101.com →
                </a>
              </div>

              {/* VelvetAngels */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">Brand Revival</Badge>
                  <Badge variant="primary" className="text-xs">Equity Stake</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">VelvetAngels.com</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Celebrity-worn shoe brand relaunching in 2026 as a full beauty and apparel brand. Best Odds Corp holds an active equity stake — involved from pitch deck to asset acquisition to corporate structuring.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['Pitch Deck', 'Web Design', 'Asset Acquisition', 'Fundraising Advisory'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://velvetangels.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit VelvetAngels.com →
                </a>
              </div>

              {/* HealthcareUnlocked */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">Healthcare</Badge>
                  <Badge variant="default" className="text-xs">E-Commerce</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">HealthcareUnlocked.com</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Healthcare company launched at the start of the 2020 COVID pandemic — distributed PPE nationwide. Built the platform, supply chain content, and provider onboarding systems under extreme time pressure.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['Platform Development', 'Healthcare SEO', 'Supply Chain Content', 'Provider Onboarding'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://healthcareunlocked.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit HealthcareUnlocked.com →
                </a>
              </div>

              {/* SkyhookCapital */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">Real Estate</Badge>
                  <Badge variant="default" className="text-xs">Finance</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">SkyhookCapital.io</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Real estate company using creative finance strategies — subject-to, owner finance, wrap-around mortgages, and lease-to-own. Built the platform architecture, financial content strategy, and lead generation systems.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['Platform Architecture', 'Financial Content', 'Investor Relations', 'SEO & Lead Gen'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://skyhookcapital.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit SkyhookCapital.io →
                </a>
              </div>

              {/* MacPokerOnline — flagship niche media */}
              <div className="reveal group border border-border hover:border-primary transition-all duration-300 bg-surface-1 p-6 sm:p-8 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="default" className="text-xs">Niche Media</Badge>
                  <Badge variant="default" className="text-xs">Since 2005</Badge>
                </div>
                <h3 className="text-h4 mb-3 group-hover:text-primary transition-colors">MacPokerOnline.com</h3>
                <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                  Flagship niche media property built in 2005. Premier destination for Mac users seeking niche software platforms — comprehensive reviews, compatibility guides, and SEO-driven organic growth over 20+ years.
                </p>
                <div className="flex flex-wrap gap-2 mb-5 sm:mb-6">
                  {['SEO Strategy', 'Content Marketing', 'Performance Marketing', 'Technical Development'].map((tag) => (
                    <span key={tag} className="text-micro uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{tag}</span>
                  ))}
                </div>
                <a href="https://macpokeronline.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Visit MacPokerOnline.com →
                </a>
              </div>
            </div>

            {/* Mini case studies note */}
            <div className="reveal max-w-3xl mx-auto text-center mb-8 sm:mb-10 p-6 sm:p-8 border border-border bg-surface-1">
              <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-3">In Development</span>
              <p className="text-body text-muted-foreground leading-relaxed">
                Formal mini case studies — sprint snapshots, before/after transformations, and documented execution examples — are being built out now. No invented metrics. No fabricated outcomes. Real work, documented honestly.
              </p>
            </div>

            {/* CTA */}
            <div className="reveal text-center">
              <Link href="/portfolio">
                <Button variant="secondary" size="lg">
                  View Full Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* LEAD MAGNET SECTION */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-4xl mx-auto">
              <div className="reveal mb-10 sm:mb-12 text-center">
                <SectionHeader
                  eyebrow="05 — Free Resource"
                  title="90-Day Execution Sprint Blueprint"
                  description="The exact framework we use to identify bottlenecks, prioritize growth levers, and ship execution every week. Built for founders doing $500K–$5M."
                  align="center" />
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
                {/* Left: What they get */}
                <div className="reveal">
                  <h3 className="text-h4 mb-5 sm:mb-6">What you'll get:</h3>
                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start gap-3">
                      <span className="text-primary mt-1 text-xl">✓</span>
                      <div>
                        <strong className="text-foreground block mb-1">Bottleneck Diagnostic Framework</strong>
                        <span className="text-body-sm text-muted-foreground">The exact questions we use to identify where revenue is leaking and what to fix first.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary mt-1 text-xl">✓</span>
                      <div>
                        <strong className="text-foreground block mb-1">Channel Prioritization Framework</strong>
                        <span className="text-body-sm text-muted-foreground">How to decide which marketing channels to focus on (and which to ignore) at your revenue stage.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary mt-1 text-xl">✓</span>
                      <div>
                        <strong className="text-foreground block mb-1">90-Day Roadmap Builder</strong>
                        <span className="text-body-sm text-muted-foreground">Step-by-step guide to building a realistic 90-day execution plan your team can actually ship.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary mt-1 text-xl">✓</span>
                      <div>
                        <strong className="text-foreground block mb-1">Capacity Bucket Calculator</strong>
                        <span className="text-body-sm text-muted-foreground">Spreadsheet to allocate hours across Always-On, Growth, and Projects without burning out your team.</span>
                      </div>
                    </li>
                  </ul>
                  <p className="text-micro uppercase tracking-widest text-muted-foreground">
                    No spam. Unsubscribe anytime. We respect your inbox.
                  </p>
                </div>

                {/* Right: Form */}
                <div className="reveal">
                  <div className="p-6 sm:p-8 md:p-10 border border-border hover:border-primary transition-all duration-300 bg-surface-1 group">
                    {!formSubmitted ?
                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        <Input
                        label="Name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required />
                      
                        <Input
                        label="Email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required />
                      
                        <Input
                        label="Company (optional)"
                        type="text"
                        placeholder="Acme Corp"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                      
                        <Link href="/lp/90-day-blueprint">
                          <Button type="button" variant="primary" size="lg" className="w-full border-glow">
                            Get the Blueprint
                          </Button>
                        </Link>
                      </form> :

                    <div className="text-center py-12">
                        <div className="text-h3 text-primary mb-4">✓ Check your email</div>
                        <p className="text-body text-muted-foreground">
                          We just sent you the 90-Day Execution Sprint Blueprint.
                        </p>
                      </div>
                    }
                  </div>
                </div>
              </div>
              {/* Secondary CTA — Free Templates Library */}
              <div className="mt-6 sm:mt-8 text-center">
                <p className="text-body-sm text-muted-foreground">
                  Not ready for the blueprint?{' '}
                  <Link href="/diy" className="text-primary hover:text-primary-hover underline underline-offset-2 transition-colors font-medium">
                    Browse all free templates →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED TOOL SECTION */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-12 sm:mb-16">
              <SectionHeader
                eyebrow="06 — Free Tools"
                title="Built by Best Odds Corp"
                description="Practical tools built in-house to solve real problems. Free to use."
                align="center" />
            </div>

            <div className="reveal max-w-4xl mx-auto">
              {/* Featured Tool Card */}
              <div className="border border-border bg-surface-1 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Left: Tool Info */}
                  <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
                        <Badge variant="primary" className="text-xs mb-3">Featured Tool</Badge>
                        <Badge variant="default" className="text-xs">Chrome Extension</Badge>
                        <Badge variant="default" className="text-xs">Free</Badge>
                      </div>
                      <h3 className="text-h2 mb-4">X Cleanup Console</h3>
                      <p className="text-body text-muted-foreground mb-6 sm:mb-8 leading-relaxed">
                        Bulk-delete your posts on X faster with preview mode, smart controls, and optional Pro filters. Built by Best Odds Corp to solve a real problem.
                      </p>
                      <ul className="space-y-3 mb-8 sm:mb-10">
                        {['Preview visible posts before deleting', 'Bulk delete from visible timeline', 'Skip pinned post', 'Delay controls', 'Pro: keyword & date filters'].map((feature) =>
                        <li key={feature} className="flex items-start gap-2 text-body-sm text-muted-foreground">
                            <span className="text-primary mt-0.5 font-bold">•</span>
                            <span>{feature}</span>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <Link href="/tools/x-cleanup-console">
                        <Button variant="primary" size="md" className="w-full sm:w-auto border-glow">
                          Learn More
                        </Button>
                      </Link>
                      <Link href="/tools">
                        <Button variant="secondary" size="md" className="w-full sm:w-auto">
                          View All Tools
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Right: Visual accent panel */}
                  <div className="relative bg-black border-l border-border flex items-center justify-center p-8 sm:p-12 min-h-[280px] sm:min-h-[320px]">
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute top-8 right-8 w-64 h-64 bg-primary/10 rounded-full blur-[80px]"></div>
                      <div className="absolute bottom-8 left-8 w-48 h-48 bg-primary/5 rounded-full blur-[60px]"></div>
                    </div>
                    <div className="relative z-10 text-center">
                      <div className="text-7xl mb-6">🧹</div>
                      <div className="text-label uppercase tracking-widest text-muted-foreground mb-2">X Cleanup Console</div>
                      <div className="text-micro uppercase tracking-widest text-primary">Chrome Extension · Free</div>
                      <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                        <div className="p-4 border border-border bg-surface-1/50">
                          <div className="text-h4 text-primary mb-1">Free</div>
                          <div className="text-micro text-muted-foreground uppercase tracking-widest">Tier</div>
                        </div>
                        <div className="p-4 border border-border bg-surface-1/50">
                          <div className="text-h4 text-primary mb-1">Pro</div>
                          <div className="text-micro text-muted-foreground uppercase tracking-widest">Upgrade</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 text-center">
                <Link href="/tools" className="text-primary hover:text-primary-hover text-body-sm underline transition-colors">
                  Browse all free tools →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* INSIGHTS PREVIEW */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-12 sm:mb-16">
              <SectionHeader
                eyebrow="07 — Insights"
                title="Strategic content for founders who execute"
                description="Bottleneck diagnosis, execution systems, AI leverage, and sprint-first frameworks — written for founder-led service businesses, not generic agency audiences."
                align="center" />
              
            </div>
            <div className="reveal max-w-3xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
                {/* Card 1: Revenue Leaks */}
<Link href="/insights/five-revenue-leaks-founder-businesses" className="group">
                  <div className="border border-border hover:border-primary transition-all duration-300 bg-surface-1 h-full flex flex-col">
                    <div className="p-6 sm:p-8 flex-1 flex flex-col">
                      <div className="mb-4">
                        <Badge variant="primary" className="text-xs mb-3">Bottlenecks & Revenue Leaks</Badge>
                        <div className="text-micro uppercase tracking-widest text-muted-foreground mb-2">March 20, 2024</div>
                      </div>
                      <h3 className="text-h4 mb-3 sm:mb-4 group-hover:text-primary transition-colors">
                        The 5 revenue leaks killing most founder-led service businesses
                      </h3>
                      <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                        Most founders assume their growth problem is a traffic problem. It&apos;s not. The real leaks are in follow-up, conversion infrastructure, and broken handoffs.
                      </p>
                      <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                        <span className="text-label uppercase tracking-widest">Read Article</span>
                        <span className="text-xl">→</span>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Card 2: Diagnostic Sprint */}
<Link href="/insights/diagnostic-sprint-what-to-expect" className="group">
                  <div className="border border-border hover:border-primary transition-all duration-300 bg-surface-1 h-full flex flex-col">
                    <div className="p-6 sm:p-8 flex-1 flex flex-col">
                      <div className="mb-4">
                        <Badge variant="default" className="text-xs mb-3">Build vs Retainer vs Sprint</Badge>
                        <div className="text-micro uppercase tracking-widest text-muted-foreground mb-2">April 15, 2024</div>
                      </div>
                      <h3 className="text-h4 mb-3 sm:mb-4 group-hover:text-primary transition-colors">
                        What happens in a diagnostic sprint (and why it&apos;s the right first move)
                      </h3>
                      <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                        Before we execute anything, we diagnose. Here&apos;s exactly what a diagnostic sprint looks like and what you get at the end.
                      </p>
                      <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                        <span className="text-label uppercase tracking-widest">Read Article</span>
                        <span className="text-xl">→</span>
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Card 3: AI for Operators */}
<Link href="/insights/ai-automation-for-operators" className="group">
                  <div className="border border-border hover:border-primary transition-all duration-300 bg-surface-1 h-full flex flex-col">
                    <div className="p-6 sm:p-8 flex-1 flex flex-col">
                      <div className="mb-4">
                        <Badge variant="default" className="text-xs mb-3">AI + Automation for Operators</Badge>
                        <div className="text-micro uppercase tracking-widest text-muted-foreground mb-2">March 25, 2024</div>
                      </div>
                      <h3 className="text-h4 mb-3 sm:mb-4 group-hover:text-primary transition-colors">
                        AI for operators: what actually works in 2024 (and what&apos;s still hype)
                      </h3>
                      <p className="text-body-sm text-muted-foreground mb-5 sm:mb-6 flex-1">
                        AI tools are everywhere. Most of them are toys. Here&apos;s what founder-operators are actually using to compress execution time and build leverage.
                      </p>
                      <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all">
                        <span className="text-label uppercase tracking-widest">Read Article</span>
                        <span className="text-xl">→</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6">
          <div className="max-w-[1600px] mx-auto text-center">
            <div className="reveal">
              <p className="text-label uppercase tracking-widest text-muted-foreground mb-5 sm:mb-6">For founders doing $500K–$5M/yr · Real estate · Med spas · Law firms · Hospitality</p>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 sm:mb-8">
                Build the roadmap{' '}
                <span className="text-primary neon-glow">first.</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-5 sm:mb-6 leading-relaxed">
                Start with a Sprint. We'll identify your biggest revenue leak and show you exactly how we'd execute the next 90 days — no fluff, no decks, just a clear plan.
              </p>
              <p className="text-body-sm text-muted-foreground max-w-xl mx-auto mb-10 sm:mb-12">
                No pressure to commit to a monthly package before we understand your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Link href="/book">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto border-glow">
                    Book a Sprint — Start With a Diagnostic
                  </Button>
                </Link>
                <Link href="/pricing">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                    See What a Sprint Uncovers
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>);


}